import { HomeStatus } from '@toro/common/shared/enum/home-status.enum';

/** Configuração do evento e do comportamento do tracker. */
export const HOME_SITUATION_TRACKER = {
  productAreaOriginFallback: 'Home',
  timeResponsavel: 'Sky',
  element: 'Situação da home',
  defaultTrigger: 'Atualização da home',
  storageKey: 'homeSituationTracker.lastSituation',
  debounceMs: 400,
};

/** Sentido da transição na jornada da conta. */
export enum SituationMovement {
  Advance = 'Avanço',
  Regression = 'Retrocesso',
  Unchanged = 'Sem alteração',
}

/**
 * Jornada da conta, em ordem: a posição define avanço x retrocesso e o rótulo é
 * o texto enviado no evento. Fonte única de verdade (evita duplicar ordem e
 * rótulos). A ordem é explícita de propósito — não segue o valor numérico do
 * enum, que coloca "Validando documentos" antes de "Descubra seu perfil".
 * Rótulos provisórios: alinhar com a UX.
 */
const JOURNEY: ReadonlyArray<{ status: HomeStatus; label: string }> = [
  { status: HomeStatus.UserNotFilling, label: 'Não iniciou' },
  { status: HomeStatus.UserIdentified, label: 'Terminar cadastro' },
  { status: HomeStatus.RegisteredWithoutDocs, label: 'Validando dados' },
  { status: HomeStatus.RegisteredSendDocs, label: 'Enviar documentos' },
  { status: HomeStatus.RegisteredDocsSendedWithoutSuitability, label: 'Descubra seu perfil' },
  { status: HomeStatus.RegisteredDocsSended, label: 'Validando documentos' },
  { status: HomeStatus.RegisteredExternalIssuer, label: 'Pendência externa' },
  { status: HomeStatus.RegisteredWithoutMoney, label: 'Conta aberta' },
  { status: HomeStatus.Activated, label: 'Conta ativa' },
];

/** "Conta ativa (saldo negativo)" é a mesma etapa de jornada que "Conta ativa". */
export function canonicalSituation(status: HomeStatus): HomeStatus {
  return status === HomeStatus.ActivatedNegative ? HomeStatus.Activated : status;
}

function journeyRank(status: HomeStatus): number {
  return JOURNEY.findIndex((step) => step.status === canonicalSituation(status));
}

export function resolveSituationLabel(status: HomeStatus): string {
  const rank = journeyRank(status);
  return rank >= 0 ? JOURNEY[rank].label : String(status);
}

export function resolveSituationMovement(
  previous: HomeStatus,
  next: HomeStatus
): SituationMovement {
  const from = journeyRank(previous);
  const to = journeyRank(next);
  if (from < 0 || to < 0 || from === to) {
    return SituationMovement.Unchanged;
  }
  return to > from ? SituationMovement.Advance : SituationMovement.Regression;
}

/** Valores numéricos válidos do enum (fonte única: o próprio HomeStatus). */
const KNOWN_SITUATIONS = new Set<number>(
  Object.values(HomeStatus).filter(
    (value): value is number => typeof value === 'number'
  )
);

/**
 * Converte um valor persistido (storage) em HomeStatus, descartando vazios,
 * texto ou números fora do enum. Dado de borda é sempre tratado como não
 * confiável.
 */
export function parseStoredSituation(raw: string | null): HomeStatus | null {
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return KNOWN_SITUATIONS.has(parsed) ? (parsed as HomeStatus) : null;
}
